/**
 * PostSnapshot represents a complete point-in-time snapshot of a post
 * and all its related data for hashing and verification purposes.
 */
export interface PostSnapshot {
  post: {
    id: string;
    content: string;
    spaceId: number | null;
    parentPostId: string | null;
    anonymous: boolean;
    createdAt: number;
  };
  uploads: Array<{
    id: string;
    key: string;
    type: string;
    createdAt: number;
  }>;
  factiiis: Array<{
    countId: string;
    factiiiId: number;
    count: number;
    createdAt: number;
    postFactiiis: Array<{
      id: string;
      data: string | null;
      // INTEGER_REQUIRED's value. Absent rather than null when unset, so a
      // snapshot hashed before the column existed serializes the same.
      number?: number;
      anonymous: boolean;
      createdAt: number;
      spaceTimes: Array<{
        id: string;
        timestamp: number | null;
        latitude: number | null;
        longitude: number | null;
        display: string | null;
        altitude: number | null;
        createdAt: number;
      }>;
      uploads: Array<{
        uploadId: string;
        key: string;
        type: string;
        createdAt: number;
      }>;
      // Post references (POST_REFERENCE_REQUIRED)
      postReferences: Array<{
        postUuid: string;
        createdAt: number;
      }>;
      // Factiii references (SOURCE_REQUIRED)
      factiiiReferences: Array<{
        factiiiId: number;
        data: string | null;
        createdAt: number;
      }>;
    }>;
  }>;
  createdAt: number;
}

/**
 * Sorts arrays in snapshot for consistent hashing.
 * Arrays are sorted by createdAt ascending, then by id/key for stability.
 * Exported for use by server verification.
 */
export function normalizeSnapshot(snapshot: PostSnapshot): PostSnapshot {
  return {
    ...snapshot,
    uploads: [...snapshot.uploads].sort((a, b) => {
      const timeCompare = a.createdAt - b.createdAt;
      return timeCompare !== 0 ? timeCompare : a.id.localeCompare(b.id);
    }),
    factiiis: [...snapshot.factiiis]
      .sort((a, b) => {
        const timeCompare = a.createdAt - b.createdAt;
        return timeCompare !== 0
          ? timeCompare
          : a.countId.localeCompare(b.countId);
      })
      .map((f) => ({
        ...f,
        postFactiiis: [...f.postFactiiis]
          .sort((a, b) => {
            const timeCompare = a.createdAt - b.createdAt;
            return timeCompare !== 0 ? timeCompare : a.id.localeCompare(b.id);
          })
          .map((pf) => ({
            ...pf,
            spaceTimes: [...pf.spaceTimes].sort((a, b) => {
              const timeCompare = a.createdAt - b.createdAt;
              return timeCompare !== 0 ? timeCompare : a.id.localeCompare(b.id);
            }),
            uploads: [...pf.uploads].sort((a, b) => {
              const timeCompare = a.createdAt - b.createdAt;
              return timeCompare !== 0
                ? timeCompare
                : a.uploadId.localeCompare(b.uploadId);
            }),
            postReferences: [...pf.postReferences].sort((a, b) => {
              const timeCompare = a.createdAt - b.createdAt;
              return timeCompare !== 0
                ? timeCompare
                : a.postUuid.localeCompare(b.postUuid);
            }),
            factiiiReferences: [...pf.factiiiReferences].sort((a, b) => {
              const timeCompare = a.createdAt - b.createdAt;
              return timeCompare !== 0
                ? timeCompare
                : a.factiiiId - b.factiiiId;
            })
          }))
      }))
  };
}
